import classnames from 'classnames';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import PaneResizeRail from './PaneResizeRail.react';
import {toPaneBounds} from './paneShares';
import {PanelMinWidthContext} from './panelMinWidth';
import {usePaneWidths} from './usePaneWidths';

interface FusedPanesProps {
  list?: React.ReactNode;
  isListOpen: boolean;
  question: React.ReactNode;
  /** Floats over the question pane's bottom-right corner. */
  questionOverlay?: React.ReactNode;
  panel?: React.ReactNode;
  isPanelOpen: boolean;
  /** Pixels the list opens at. */
  listDefaultWidth?: number;
  /** Pixels the list can be dragged down to, in place of its 20% floor. */
  listMinWidth?: number;
}

/**
 * One card holding the question list, the question, and a side panel, split by resize rails.
 * Side panes are sized as shares of the card; the question takes the rest. Closed panes stay
 * mounted so their tracks animate to and from 0. The panel's content can set the panel's floor
 * with `useReportPanelMinWidth`.
 */
const FusedPanes = ({
  list,
  isListOpen,
  question,
  questionOverlay,
  panel,
  isPanelOpen,
  listDefaultWidth,
  listMinWidth
}: FusedPanesProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState<number | null>(null);
  // What the panel's content reports it needs; see `useReportPanelMinWidth`.
  const [panelFloors, setPanelFloors] = useState<Record<string, number>>({});
  const reportPanelFloor = useCallback((id: string, width: number | null) => {
    setPanelFloors((current) => {
      if (width === null) {
        if (!(id in current)) return current;
        const {[id]: _removed, ...rest} = current;
        return rest;
      }
      return current[id] === width ? current : {...current, [id]: width};
    });
  }, []);
  const floorWidths = Object.values(panelFloors);
  const panelMinWidth = floorWidths.length ? Math.max(...floorWidths) : null;
  const showList = Boolean(list) && isListOpen;
  const showPanel = Boolean(panel) && isPanelOpen;

  const hasPixelBounds = Boolean(listDefaultWidth || listMinWidth || panelMinWidth);

  // Layout effect, so the first paint already has the list at its default width.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || !hasPixelBounds || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setCardWidth(card.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(card);
    return () => observer.disconnect();
  }, [hasPixelBounds]);

  const bounds = toPaneBounds(cardWidth, {
    listMinWidth,
    listDefaultWidth,
    panelMinWidth: panelMinWidth ?? undefined
  });

  // Measuring forces a style pass at the unmeasured width, so animating from the first paint would
  // slide the list open on load. Turn the animation on a frame after the measured width lands.
  const isMeasured = !hasPixelBounds || cardWidth !== null;
  const [canAnimate, setCanAnimate] = useState(false);
  useEffect(() => {
    if (!isMeasured) return undefined;
    const frame = requestAnimationFrame(() => setCanAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, [isMeasured]);
  const panes = usePaneWidths({isListOpen: showList, isPanelOpen: showPanel}, bounds);

  const railProps = (pane: 'list' | 'panel') => ({
    share: panes.shares[pane],
    ...panes.getLimits(pane),
    containerRef: cardRef,
    isDragging: panes.draggingPane === pane,
    onDraggingChange: (isDragging: boolean) => panes.setDraggingPane(isDragging ? pane : null),
    onResize: (share: number) => panes.resize(pane, share),
    onReset: () => panes.reset(pane)
  });

  return (
    <div
      ref={cardRef}
      style={panes.cardStyle}
      className={classnames(
        '@container grid grid-rows-[100%] overflow-hidden rounded-x1 border border-solid border-subtle background-primary [&_.question-wrapper]:rounded-none [&_.question-wrapper]:border-0',
        panes.draggingPane && 'cursor-ew-resize select-none',
        canAnimate &&
          !panes.draggingPane &&
          'transition-[grid-template-columns] duration-250 ease-linear motion-reduce:transition-none'
      )}
    >
      {list ? (
        <div
          inert={!isListOpen}
          className='min-w-x0 isolate col-start-1 row-start-1 flex overflow-hidden'
        >
          <div className='flex shrink-0' style={panes.contentStyle('list')}>
            {list}
          </div>
        </div>
      ) : null}
      {showList ? (
        <PaneResizeRail
          label='Resize question list'
          paneSide='start'
          className='col-start-2 row-start-1'
          {...railProps('list')}
        />
      ) : null}
      <div className='min-w-x0 relative isolate col-start-3 row-start-1 flex overflow-hidden'>
        {question}
        {questionOverlay ? (
          <div className='right-x2 bottom-x2 z-raised absolute'>{questionOverlay}</div>
        ) : null}
      </div>
      {showPanel ? (
        <PaneResizeRail
          label='Resize side panel'
          paneSide='end'
          className='col-start-4 row-start-1'
          {...railProps('panel')}
        />
      ) : null}
      {panel ? (
        <aside
          inert={!isPanelOpen}
          className='min-w-x0 isolate col-start-5 row-start-1 flex justify-end overflow-hidden'
          data-testid='practice-view__right-side-panel'
        >
          <div className='flex shrink-0' style={panes.contentStyle('panel')}>
            <PanelMinWidthContext value={reportPanelFloor}>{panel}</PanelMinWidthContext>
          </div>
        </aside>
      ) : null}
    </div>
  );
};

export default FusedPanes;
